/**
 * Casos de uso del cliente. Toda la lógica de reglas vive en @habitos/engine;
 * aquí solo se traducen las intenciones del usuario a escrituras.
 */
import {
  addDays,
  checkMove,
  evaluateHabit,
  ueKeyForDate,
  type LocalDate,
} from '@habitos/engine';
import { PLAN_LIMITS, SETTINGS_ID, countActiveHabits, moveId, overrideId, type PlanId, type Settings } from '@habitos/protocol';
import { getMeta } from '../db/db';
import { write, writeMany, type WriteRequest } from '../db/repo';
import { uuidv7 } from '../lib/ids';
import { keyAfter, keyBetween } from '../lib/fractional';
import {
  currentWeekStartOf,
  toEngineSettings,
  toHabitDef,
  type EntryRec,
  type GroupRec,
  type HabitRec,
  type MoveRec,
  type OverrideRec,
  type VacationRec,
} from './model';

export type { Ctx } from '@habitos/assistant/domain/data';
import type { Ctx } from '@habitos/assistant/domain/data';
export { dayTotal, DomainError, draftFromHabit, emptyDraft, nextVersions, PlanLimitError, type HabitDraft } from '@habitos/assistant/domain/records';
import { assertLoggable, dayTotal, DomainError, draftFromHabit, habitEditPatch, logWrites, newHabitRecord, nextVersions, PlanLimitError, type HabitDraft } from '@habitos/assistant/domain/records';



export async function currentPlan(): Promise<PlanId> {
  const ent = await getMeta<{ plan: PlanId; validUntil: number } | null>('entitlement', null);
  if (ent && ent.plan === 'premium' && ent.validUntil > Date.now()) return 'premium';
  return 'free';
}

export function defaultWeekStart(locale: string): number {
  try {
    const info = (new Intl.Locale(locale) as unknown as { weekInfo?: { firstDay: number }; getWeekInfo?: () => { firstDay: number } });
    const wi = info.getWeekInfo?.() ?? info.weekInfo;
    if (wi && wi.firstDay >= 1 && wi.firstDay <= 7) return wi.firstDay;
  } catch {
    /* sin soporte: valor por defecto */
  }
  return locale.toLowerCase().startsWith('en-us') ? 7 : 1;
}

export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function defaultSettings(locale: string, tz = deviceTimeZone()): Settings {
  return {
    dayStartMinutes: 240,
    weekStartInitial: defaultWeekStart(locale),
    weekStartChanges: [],
    tzMode: 'follow_device',
    homeTz: tz,
    effectiveTz: tz,
    hideStreaks: false,
    quietStart: null,
    quietEnd: null,
  };
}

export async function createSettings(s: Settings): Promise<void> {
  await write('settings', SETTINGS_ID, s);
}

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  await write('settings', SETTINGS_ID, patch);
}

/** Cambio de inicio de semana con historial (FR-TIME-05). */
export async function changeWeekStart(settings: Settings, weekStart: number, today: LocalDate): Promise<void> {
  if (currentWeekStartOf(settings) === weekStart) return;
  const changes = settings.weekStartChanges.filter((c) => c.changedOn !== today);
  const prevRule = changes.length ? changes[changes.length - 1]!.weekStart : settings.weekStartInitial;
  if (prevRule !== weekStart) changes.push({ changedOn: today, weekStart });
  await updateSettings({ weekStartChanges: changes });
}





export async function createHabit(d: HabitDraft, habits: HabitRec[]): Promise<string> {
  const plan = await currentPlan();
  const limit = PLAN_LIMITS[plan].activeHabits;
  if (countActiveHabits(habits) >= limit) throw new PlanLimitError(limit);
  const id = uuidv7();
  const last = habits
    .filter((h) => !h.deletedAt)
    .map((h) => h.sortKey)
    .sort()
    .pop();
  await write('habits', id, newHabitRecord(d, keyAfter(last ?? null), new Date().toISOString()));
  return id;
}



export interface HistoryImpact {
  changed: number;
}

/** Cuántas UE pasadas cambiarían de estado si se aplica la edición al historial. */
export function historyImpact(
  h: HabitRec,
  d: HabitDraft,
  ctx: Ctx,
  entries: EntryRec[],
  overrides: OverrideRec[],
  vacations: VacationRec[],
): HistoryImpact {
  const settings = toEngineSettings(ctx.settings);
  const before = evaluateHabit({ habit: toHabitDef(h), entries, overrides, vacations, settings, today: ctx.today });
  const after = evaluateHabit({
    habit: { ...toHabitDef(h), startDate: d.startDate, endDate: d.endDate, versions: nextVersions(h, d, ctx.today, true) },
    entries,
    overrides,
    vacations,
    settings,
    today: ctx.today,
  });
  const map = new Map(before.map((r) => [r.key, r.status]));
  let changed = 0;
  for (const r of after) if (map.get(r.key) !== r.status) changed++;
  changed += Math.max(0, before.length - after.length);
  return { changed };
}

export async function updateHabit(h: HabitRec, d: HabitDraft, ctx: Ctx, applyToHistory: boolean): Promise<void> {
  await write('habits', h.id, habitEditPatch(h, d, ctx.today, applyToHistory));
}

export async function duplicateHabit(h: HabitRec, habits: HabitRec[], suffix: string): Promise<string> {
  const d = draftFromHabit(h);
  return createHabit({ ...d, name: `${h.name} ${suffix}`.slice(0, 80) }, habits);
}




/**
 * Registra una cantidad (positiva o negativa) en un día lógico. El total
 * del día no puede quedar por debajo de 0 (FR-LOG-02). Registrar en un día
 * omitido retira la omisión (FR-LOG-06).
 */
export async function logAmount(
  h: HabitRec,
  date: LocalDate,
  delta: number,
  ctx: Ctx,
  entries: EntryRec[],
  overrides: OverrideRec[],
  note: string | null = null,
): Promise<void> {
  await writeMany(logWrites(h, date, delta, ctx, entries, overrides, { newId: () => uuidv7(), nowIso: new Date().toISOString() }, note));
}

/** Marca o desmarca un hábito binario en un día (FR-LOG-01). */
export async function toggleBinary(h: HabitRec, date: LocalDate, ctx: Ctx, entries: EntryRec[], overrides: OverrideRec[]): Promise<boolean> {
  assertLoggable(h, date, ctx);
  if (dayTotal(entries, h.id, date) >= 1) {
    const now = new Date().toISOString();
    await writeMany(
      entries
        .filter((e) => e.habitId === h.id && e.logicalDate === date && !e.deletedAt)
        .map((e) => ({ entity: 'entries' as const, id: e.id, patch: { deletedAt: now } })),
    );
    return false;
  }
  await logAmount(h, date, 1, ctx, entries, overrides);
  return true;
}

export async function deleteEntry(id: string): Promise<void> {
  await write('entries', id, { deletedAt: new Date().toISOString() });
}

export async function editEntry(id: string, patch: { value?: number; note?: string | null }): Promise<void> {
  await write('entries', id, patch);
}

/** Omitir o marcar como fallado la UE que contiene `date`; null retira la marca. */
export async function setOverride(h: HabitRec, date: LocalDate, kind: 'skipped' | 'failed' | null, ctx: Ctx, reason: string | null = null): Promise<void> {
  assertLoggable(h, date, ctx);
  // shortcut: no se pasan los traslados, así que un día ya trasladado se puede omitir o
  // fallar desde una vista sin actualizar. La anulación queda huérfana y el motor la ignora
  // (no hay UE con esa clave). Pasar `moves` si alguna vista deja de filtrar días no programados.
  const ueKey = ueKeyForDate(toHabitDef(h), toEngineSettings(ctx.settings), date);
  if (!ueKey) throw new DomainError('not_scheduled');
  const id = overrideId(h.id, ueKey);
  if (kind === null) {
    await write('overrides', id, { habitId: h.id, ueKey, kind: 'skipped', reason: null, deletedAt: new Date().toISOString() });
    return;
  }
  await write('overrides', id, { habitId: h.id, ueKey, kind, reason, deletedAt: null });
}

export async function archiveHabit(h: HabitRec, ctx: Ctx, archived: boolean): Promise<void> {
  await write('habits', h.id, { archivedAt: archived ? ctx.today : null });
}

export async function deleteHabit(h: HabitRec, deleted: boolean): Promise<void> {
  await write('habits', h.id, { deletedAt: deleted ? new Date().toISOString() : null });
}

/** Reprograma una tarea a otra fecha (y hora opcional). Devuelve el estado anterior para deshacer. */
export async function rescheduleTask(h: HabitRec, dueDate: LocalDate | null, dueTime: string | null): Promise<{ dueDate: LocalDate | null; dueTime: string | null }> {
  const previous = { dueDate: h.dueDate, dueTime: h.dueTime ?? null };
  await write('habits', h.id, { dueDate, dueTime: dueDate ? dueTime : null });
  return previous;
}

export async function setTaskDone(h: HabitRec, done: boolean): Promise<void> {
  await write('habits', h.id, { completedAt: done ? new Date().toISOString() : null });
}

/** Mueve un hábito entre dos vecinos (claves fraccionales). */
export async function moveHabit(id: string, before: string | null, after: string | null): Promise<void> {
  await write('habits', id, { sortKey: keyBetween(before, after) });
}

export async function createGroup(name: string, color: string, groups: GroupRec[]): Promise<string> {
  const id = uuidv7();
  const last = groups
    .filter((g) => !g.deletedAt)
    .map((g) => g.sortKey)
    .sort()
    .pop();
  await write('groups', id, { name: name.trim(), color, sortKey: keyAfter(last ?? null), deletedAt: null });
  return id;
}

export async function updateGroup(id: string, patch: { name?: string; color?: string; sortKey?: string }): Promise<void> {
  await write('groups', id, patch);
}

export async function deleteGroup(id: string, habits: HabitRec[]): Promise<void> {
  const reqs: WriteRequest[] = [{ entity: 'groups', id, patch: { deletedAt: new Date().toISOString() } }];
  for (const h of habits) {
    if (h.primaryGroupId === id || h.secondaryGroupIds.includes(id)) {
      reqs.push({
        entity: 'habits',
        id: h.id,
        patch: {
          primaryGroupId: h.primaryGroupId === id ? null : h.primaryGroupId,
          secondaryGroupIds: h.secondaryGroupIds.filter((g) => g !== id),
        },
      });
    }
  }
  await writeMany(reqs);
}

/**
 * Traslada la ocurrencia de `from` a `to` (FR-SCH-12). El motor decide si es válido;
 * aquí solo se traduce a una escritura. El identificador es determinista, así que
 * trasladar el mismo día dos veces reescribe el registro en lugar de duplicarlo.
 */
export async function moveOccurrence(h: HabitRec, moves: MoveRec[], from: LocalDate, to: LocalDate, ctx: Ctx): Promise<void> {
  const reason = checkMove(toHabitDef(h), toEngineSettings(ctx.settings), moves, from, to);
  if (reason) throw new DomainError(reason);
  await write('moves', moveId(h.id, from), { habitId: h.id, fromDate: from, toDate: to, deletedAt: null });
}

/** Deshace un traslado: el día vuelve a su sitio según la repetición. */
export async function undoMove(m: MoveRec): Promise<void> {
  await write('moves', m.id, { deletedAt: new Date().toISOString() });
}

export interface VacationDraft {
  name: string | null;
  startDate: LocalDate;
  endDate: LocalDate;
  scope: 'all' | 'selected';
  habitIds: string[];
}

export async function saveVacation(id: string | null, v: VacationDraft): Promise<string> {
  const vid = id ?? uuidv7();
  await write('vacations', vid, { ...v, name: v.name?.trim() || null, deletedAt: null });
  return vid;
}

export async function deleteVacation(id: string): Promise<void> {
  await write('vacations', id, { deletedAt: new Date().toISOString() });
}

export async function saveReminder(
  id: string | null,
  r: { habitId: string; time: string; days: number[] | null; enabled: boolean },
  existingForHabit: number,
): Promise<string> {
  if (!id) {
    const plan = await currentPlan();
    if (existingForHabit >= PLAN_LIMITS[plan].remindersPerHabit) throw new PlanLimitError(PLAN_LIMITS[plan].remindersPerHabit);
  }
  const rid = id ?? uuidv7();
  await write('reminders', rid, { ...r, deletedAt: null });
  return rid;
}

export async function deleteReminder(id: string): Promise<void> {
  await write('reminders', id, { deletedAt: new Date().toISOString() });
}

export function yesterday(ctx: Ctx): LocalDate {
  return addDays(ctx.today, -1);
}
